import { Component, HostListener, Inject, OnInit, Renderer2 } from '@angular/core';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { LoadingIndicatorComponent } from 'components/loading-indicator/loading-indicator.component';
import { ToastContainerComponent } from 'components/toast-container/toast-container.component';
import { HeaderComponent } from 'components/header/header.component';
import { FooterComponent } from 'components/footer/footer.component';
import { NgClass, DOCUMENT } from '@angular/common';
import { AuthorizationService } from 'services/authorization.service';
import { environment } from 'env/environment';
import { AutoLogoutModalComponent } from 'components/modal/auto-logout-modal/auto-logout-modal.component';
import { filter } from 'rxjs/operators';
import { KEY_LAST_ACTIVE_SECTION } from 'pages/workspace-page/workspace-page.component';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
export const VARIABLE_VIEWPORT_WIDTH = '--real-viewport-width';
export const DESKTOP_BREAKPOINT = 768;

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    LoadingIndicatorComponent,
    ToastContainerComponent,
    HeaderComponent,
    FooterComponent,
    NgClass,
    AutoLogoutModalComponent
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  title = 'LNA';
  subscriptions: Subscription[] = [];
  get isAuthenticated(): boolean {
    return this.authService.isAuthenticated() && Object.keys(this.authService.userSecurityInfo()).length > 0;
  }

  @HostListener('window:resize', [])
  onResize() {
    this.setViewportSize();
  }

  constructor(
    private authService: AuthorizationService,
    private renderer: Renderer2,
    private router: Router,
    private httpClient: HttpClient,
    private translate: TranslateService,
    @Inject(DOCUMENT) private document: Document
  ) {
    // Remove local storage items used for redirects when navigating away from intended flow
    this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      if (
        !event.urlAfterRedirects.includes('/pakalpojumi/lasitavas-apmeklejums') &&
        !event.urlAfterRedirects.includes('/pieteikums/') &&
        (event.urlAfterRedirects as string).split('?')[0] !== '/'
      ) {
        sessionStorage.removeItem(KEY_LAST_ACTIVE_SECTION);
      }
    });
  }

  ngOnInit(): void {
    const lang = 'lv';

    this.httpClient.get(`${environment.apiUrl}/api/tooltip/translation/${lang}`).subscribe((remoteTranslations) => {
      this.translate.setTranslation(lang, this.normalizeFlatJson(remoteTranslations), true);
    });

    this.authService.getVersionInfo().subscribe((version) => {
      localStorage.setItem('versionInfo', JSON.stringify(version));
    });

    this.setViewportSize();

    document.documentElement.style.setProperty('--bs-primary-rgb', '140,8,12');
    this.addMatomo();
  }

  addMatomo() {
    if (environment.useMatomo === 'true') {
      const script = this.renderer.createElement('script');

      script.type = 'text/javascript';
      script.innerHTML = `
      var _paq = window._paq = window._paq || [];
      /* tracker methods like "setCustomDimension" should be called before "trackPageView" */
      _paq.push(['trackPageView']);
      _paq.push(['enableLinkTracking']);
      (function() {
        var u='https://statistika.kis.gov.lv/';
        _paq.push(['setTrackerUrl', u+'matomo.php']);
        _paq.push(['setSiteId', '${environment.matomoKey}']);
        var d=document, g=d.createElement('script'), s=d.getElementsByTagName('script')[0];
        g.async=true; g.src=u+'matomo.js'; s.parentNode.insertBefore(g,s);
      })();`;

      this.renderer.appendChild(this.document.head, script);
    }
  }

  setViewportSize() {
    environment.isMobile = window.innerWidth < DESKTOP_BREAKPOINT;
    document.documentElement.style.setProperty(VARIABLE_VIEWPORT_WIDTH, `${document.body.clientWidth}px`);
  }

  normalizeFlatJson(flatJson: Record<string, any>): Record<string, any> {
    const nestedJson: Record<string, any> = {};

    Object.keys(flatJson).forEach((key) => {
      const value = flatJson[key];
      const keyParts = key.split('.');

      keyParts.reduce((acc, part, index) => {
        if (index === keyParts.length - 1) {
          acc[part] = value;
        } else {
          acc[part] = acc[part] || {};
        }
        return acc[part];
      }, nestedJson);
    });

    return nestedJson;
  }
}
